<template>
  <span
    v-if="isBrokenReference && isPreviewEnabled"
    style="
      color: #b00;
      background: #fff3f3;
      border: 1px dashed #b00;
      padding: 2px 6px;
      font-size: 0.85em;
    "
  >
    [Broken link: {{ brokenEntryId }}]
  </span>
  <InlineTypeformSurvey
    v-else-if="isTypeform && typeformId"
    :typeform-id="typeformId"
    :button-props="TypeformButtonProps"
    :button-text="
      isEntryOfContentType(item, 'link') ? item.fields.linkText : undefined
    "
  >
  </InlineTypeformSurvey>
  <LinkElement v-else-if="childProps" v-bind="childProps">
    <slot v-if="useSlot"></slot>
    <template v-if="!useSlot">
      {{
        isEntryOfContentType(item, 'download')
          ? item.fields.title
          : item.fields.linkText
      }}
    </template>
  </LinkElement>
</template>

<script setup lang="ts">
import { LinkElement } from '@hypercodestudio/basler-components';
import type {
  LinkElementProps as LinkProps,
  ButtonProps
} from '@hypercodestudio/basler-components';

import type { IDownload, ILink } from '~~/lib/ContentfulService';
import { getLinkIconPosition } from '~/utils/getLinkIconPosition';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { isEmpty } from '~/utils/isEmpty';
const InlineTypeformSurvey = defineAsyncComponent(
  () => import('~/components/global/lib/InlineTypeformSurvey.vue')
);

interface Props {
  item: ILink | IDownload;
  useSlot?: boolean;
}

const props = defineProps<Props>();

const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();
const logger = useLogger();

const brokenEntryId = computed(() => {
  const item = props.item as any;
  return item?.sys?.id ?? 'unknown';
});

const isBrokenReference = computed(() => {
  const item = props.item;
  if (!item || !('fields' in item) || !item.fields) {
    const entryId = brokenEntryId.value;
    const linkType =
      (item as any)?.sys?.linkType ?? (item as any)?.sys?.type ?? 'unknown';

    logger.debug(
      'ContentfulLink',
      `Broken or unresolved link reference detected (entryId=${entryId}, linkType=${linkType}). ` +
        'The linked entry may be in draft, deleted, or not available in this locale.'
    );

    if (isPreviewEnabled && import.meta.client) {
      console.warn(
        `[Contentful Link] Broken or unresolved link reference (entryId=${entryId}, linkType=${linkType}). ` +
          'The linked entry may be in draft, deleted, or not available in this locale.'
      );
    }

    return true;
  }
  return false;
});

const hasDocument =
  isEntryOfContentType(props.item, 'link') && props.item.fields.documentId;
const hasSoftware =
  isEntryOfContentType(props.item, 'link') && props.item.fields.softwareId;

const childProps = computed<LinkProps | undefined>(() => {
  if (isBrokenReference.value) {
    return undefined;
  }

  return !hasDocument &&
    !hasSoftware &&
    isEntryOfContentType(props.item, 'download')
    ? getPropsForDownload(props.item)
    : getPropsForLink(props.item);
});

const TypeformButtonProps = computed(() =>
  isEntryOfContentType(props.item, 'link') &&
  !isEmpty(props.item.fields?.typeformForm)
    ? getPropsForTypeform(props.item)
    : undefined
);

const isTypeform = computed(() => {
  return (
    isEntryOfContentType(props.item, 'link') &&
    !isEmpty(props.item.fields?.typeformForm)
  );
});

const typeformId = computed(() => {
  if (isEntryOfContentType(props.item, 'link')) {
    return props.item.fields?.typeformForm || undefined;
  }
  return undefined;
});

function getPropsForLink(item: ILink): LinkProps {
  const link = useBuildLinkInterface(item);
  return {
    link,
    targetBlank: item.fields?.linkTarget === '_blank',
    size: item.fields?.linkStyleSize,
    styleType: item.fields?.linkStyle ?? 'default',
    iconName: item.fields?.linkIcon,
    iconType: getLinkIconPosition(item.fields?.linkIconPosition)
  };
}

function getPropsForDownload(item: IDownload): LinkProps {
  const link = useBuildLinkInterface(item);
  return {
    link,
    targetBlank: true,
    size: 'medium',
    styleType: 'default',
    iconType: 'no-icon'
  };
}

function getPropsForTypeform(item: ILink): ButtonProps {
  return {
    size: item.fields?.linkStyleSize,
    styleType: (item.fields?.linkStyle as any) ?? 'primary',
    iconName: item.fields?.linkIcon,
    iconType: getLinkIconPosition(item.fields?.linkIconPosition)
  };
}
</script>
